import { Component } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withStyles } from "@mui/styles";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InterventionReportField from "./InterventionReportField";
import "./InterventionReportBloc.css";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    blocHasChanged,
    validateBloc,
    getFieldVisibility,
    getFieldMandatory,
    setCurrentIntervention,
    userCanValidateBloc,
    setExpandedBloc,
} from "../../redux/intervention/interventionActions";
import Switch from "@mui/material/Switch";
import { throwErrorMessage } from "../../lib/error_helper";
import {
    getStateAccessor,
    evaluateDynamicPropertyOfBloc,
    mergeFieldValueAndFieldType,
} from "../../lib/evaluateStringAsCodeHelper";
import { submitInterventionInDb } from "../../redux/intervention/interventionEditionActions";
import { getCurrentFunctionName } from "../../performanceModeManager";
import { SIGNATURES_BLOC_TYPE_CODE, TIME_SLOT_BLOC_TYPE_CODE } from "../../redux/intervention/interventionConstants";
import { Checkbox } from "@mui/material";
import moment from "moment";

const styles = (theme) => ({
    root: {
        width: "100%",
    },
    heading: {
        fontSize: theme.typography.pxToRem(15),
        flexBasis: "33.33%",
        flexShrink: 0,
    },
    italic: {
        fontSize: theme.typography.pxToRem(13),
        flexBasis: "33.33%",
        flexShrink: 0,
        fontStyle: "italic",
        paddingTop: 0,
    },
    secondaryHeading: {
        fontSize: theme.typography.pxToRem(15),
        color: theme.palette.text.secondary,
    },
    AccordionSummaryContent: {
        content: {
            margin: 3,
        },
    },
});

const ValidationSwitch = withStyles({
    switchBase: {
        color: "#5887ee",
        "&$checked": {
            color: "#1057C8",
        },
    },
    checked: {},
})(Switch);

class InterventionReportBloc extends Component {
    constructor(props) {
        super(props);

        this.state = {
            expanded: this.props.expanded === this.props.currentBlocId,
            userCanValidateBloc:
                this.props.blocType &&
                userCanValidateBloc(this.props.activeUser, this.props.intervention, this.props.blocType),
        };

        if (this.props.signature && this.props.blocType && this.props.blocType.code === SIGNATURES_BLOC_TYPE_CODE) {
            this.props.setExpandedBloc(this.props.currentBlocId);
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.creationBloc) return prevState;

        let expanded = nextProps.expanded === nextProps.currentBlocId;

        if (prevState.expanded === expanded) return prevState;

        return {
            expanded,
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (nextProps.creationBloc) return false;

        if ((nextProps.expanded === nextProps.currentBlocId) !== this.state.expanded) return true;

        if (this.props.validated !== nextProps.validated) return true;

        if (this.props.signed_by_customer !== nextProps.signed_by_customer) return true;

        if (this.props.currentBlocId !== nextProps.currentBlocId) return true;

        if (!nextState.expanded) return false;

        let nextBlocValues = nextProps.blocs.find((bv) => bv.mobile_id === nextProps.currentBlocId);
        let actualBlocValues = this.props.blocs.find((bv) => bv.mobile_id === this.props.currentBlocId);

        if (!nextBlocValues || !actualBlocValues) {
            console.error("Valeurs introuvables");
            return null;
        }

        try {
            return blocHasChanged(nextBlocValues, actualBlocValues);
        } catch (err) {
            return true;
        }
    }

    onBlocHeaderClick = () => {
        if (this.props.creationBloc) {
            this.props.addBloc(false);
        } else {
            this.props.onClick(this.props.currentBlocId);
        }
    };

    fieldValueChange(fieldId, value) {
        let blocId = this.props.currentBlocId;
        let blocGraphicalIndex = this.props.index; //Index dans la liste en mode preview
        this.props.fieldValueChange(blocId, fieldId, value, blocGraphicalIndex);

        this.forceUpdate();
    }

    getBlocClasses() {
        let classes = "";

        if (this.props.expanded === this.props.currentBlocId) classes = classes.concat("expanded-bloc");

        if (this.props.blocType) {
            if (
                (this.props.signature &&
                    !this.props.blocType.require_customer_signature &&
                    this.props.blocType.code !== SIGNATURES_BLOC_TYPE_CODE) ||
                (!this.props.signature && this.props.blocType.code === SIGNATURES_BLOC_TYPE_CODE) ||
                ((this.props.controlSignature || this.props.signature) &&
                    this.props.blocType.code === TIME_SLOT_BLOC_TYPE_CODE) ||
                (this.props.preview && !this.props.blocType.display_to_customer)
            )
                classes = classes.concat(" hidden");
        }

        return classes;
    }

    getClasses() {
        let classes = "without-side-padding";

        if (!this.props.controlSignature) classes = classes.concat(" bloc-header");

        if (this.props.expanded === this.props.currentBlocId) classes = classes.concat(" expanded-bloc");

        if (!this.props.blocType || this.props.creationBloc) return classes;

        let blocs = this.props.blocs;
        let bloc = blocs.find((bv) => bv.mobile_id === this.props.currentBlocId);

        if (
            (!this.props.signature && bloc.validated) ||
            (this.props.signature && bloc.signed_by_customer && this.props.blocType.code !== SIGNATURES_BLOC_TYPE_CODE)
        ) {
            classes = classes.concat(" ok-bloc");
        }

        return classes;
    }

    validateBloc() {
        let blocs = this.props.blocs;
        let indexOfBloc = blocs.findIndex((bv) => bv.mobile_id === this.props.currentBlocId);
        let bloc = blocs[indexOfBloc];
        let blocType = this.props.blocTypes.find((bt) => bt.id === bloc.type_id);

        this.props
            .validateBloc(
                bloc,
                this.props.intervention,
                blocs,
                this.props.blocTypes,
                blocType,
                getStateAccessor(this.props),
                this.props.activeUser,
                this.props.preview || this.props.controlSignature
            )
            .then((updatedBloc) => {
                blocs[indexOfBloc] = updatedBloc;
                this.props.setCurrentIntervention(this.props.intervention, blocs);
                submitInterventionInDb(this.props.intervention, blocs);
                this.props.onClick(this.props.currentBlocId);
                this.forceUpdate();
            })
            .catch((err) => {
                console.error(
                    "Erreur pendant la validation d'un bloc. Ce n'est pas grave l'erreur s'est normalement déjà affiché si besoin",
                    err
                );
            });
    }

    signedBloc() {
        let blocs = this.props.blocs;
        let indexOfBloc = blocs.findIndex((bv) => bv.mobile_id === this.props.currentBlocId);
        let bloc = blocs[indexOfBloc];
        bloc.signed_by_customer = !bloc.signed_by_customer;

        bloc.mobile_write_date = moment().format("YYYY-MM-DD HH:mm:ss");
        bloc.mobile_write_id = this.props.activeUser.id;

        this.props.setCurrentIntervention(this.props.intervention, blocs);
        submitInterventionInDb(this.props.intervention, blocs);
    }

    deleteBloc(currentBlocId) {
        this.props.deleteBloc(currentBlocId);
    }

    render() {
        console.perfLog(getCurrentFunctionName());

        const { classes } = this.props;
        let stateAccessor = getStateAccessor(this.props);
        let blocSubTitle = "";
        let fieldsElements = [];
        let blocValues = this.props.blocs.find((bv) => bv.mobile_id === this.props.currentBlocId);

        if (!this.props.creationBloc && this.props.expanded) {
            if (!blocValues) {
                console.error("Valeurs introuvables");
                return null;
            }

            this.props.blocType.field_ids
                .sort((f) => f.sequence)
                .forEach((f) => {
                    let fieldValue = blocValues.field_ids.find((bv) => bv.type_id === f.id);

                    if (!fieldValue) {
                        console.error("La valeur du champs " + f.code + " est introuvable");
                        return null;
                    }

                    let visible = getFieldVisibility(
                        this.props.blocs,
                        this.props.blocTypes,
                        blocValues.mobile_id,
                        f,
                        stateAccessor
                    );
                    let required = getFieldMandatory(
                        this.props.blocs,
                        this.props.blocTypes,
                        blocValues.mobile_id,
                        f,
                        stateAccessor
                    );

                    let index = f.id || f.mobile_Id;

                    fieldsElements.push(
                        <InterventionReportField
                            key={index}
                            id={[this.props.id.toString(), index].join("-")}
                            fieldValueChange={this.fieldValueChange.bind(this)}
                            model={f}
                            value={fieldValue}
                            blocId={blocValues.mobile_id}
                            blocType={this.props.blocType}
                            visible={visible}
                            required={required}
                        />
                    );
                });
        }

        if (this.props.blocType) {
            let fields = mergeFieldValueAndFieldType(
                this.props.blocs,
                this.props.blocTypes,
                this.props.currentBlocId,
                this.props.blocType
            );
            blocSubTitle = evaluateDynamicPropertyOfBloc(
                fields,
                this.props.blocType,
                "display_title_formula",
                "",
                stateAccessor
            );
        }

        let expandedElement = null;

        if (this.props.expanded !== this.props.currentBlocId) {
            expandedElement = (
                <IconButton color="inherit" onClick={this.onBlocHeaderClick.bind(this)} aria-label="Étendre">
                    <ExpandMoreIcon />
                </IconButton>
            );
        } else {
            expandedElement = (
                <IconButton color="inherit" onClick={this.onBlocHeaderClick.bind(this)} aria-label="Réduire">
                    <ExpandLessIcon />
                </IconButton>
            );
        }

        return (
            <div
                className="col-lg-12 col-md-12 col-sm-12 col-12 without-side-padding"
                ref={(panel) => (this.panel = panel)}
            >
                <Accordion expanded={!this.props.creationBloc && this.state.expanded} className={this.getBlocClasses()}>
                    <AccordionSummary
                        className={this.getClasses()}
                        classes={{ content: classes.AccordionSummaryContent.content }}
                    >
                        {this.props.creationBloc ? (
                            <IconButton
                                className="padding-add-btn"
                                color="inherit"
                                onClick={this.onBlocHeaderClick.bind(this)}
                                aria-label="Ajouter"
                            >
                                <AddIcon />
                            </IconButton>
                        ) : (
                            <div className="row without-side-padding without-margin">
                                <div className="without-side-padding col-lg-2 col-md-2 col-sm-2 col-2">
                                    {expandedElement}
                                </div>
                                <div
                                    className="col-lg-6 col-md-6 col-sm-6 col-6 without-side-padding"
                                    onClick={this.onBlocHeaderClick.bind(this)}
                                >
                                    <Typography className={classes.heading}>
                                        {this.props.blocType.name +
                                            (blocValues.creator ? " (" + blocValues.creator + ")" : " (Moi)")}
                                    </Typography>
                                </div>
                                {!this.props.creationBloc && this.state.userCanValidateBloc && !this.props.signature ? (
                                    <div className="col-lg-2 col-md-2 col-sm-2 col-2 without-side-padding">
                                        <ValidationSwitch
                                            onChange={this.validateBloc.bind(this)}
                                            checked={blocValues.validated}
                                            value={"validated"}
                                            color="default"
                                        />
                                    </div>
                                ) : null}
                                {this.props.signature && this.props.blocType.code !== SIGNATURES_BLOC_TYPE_CODE ? (
                                    <div className="col-lg-2 col-md-2 col-sm-2 col-2 without-side-padding">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    onChange={this.signedBloc.bind(this)}
                                                    checked={blocValues.signed_by_customer}
                                                    value={"signed_by_customer"}
                                                    color="default"
                                                />
                                            }
                                            label="Signé"
                                        />
                                    </div>
                                ) : null}
                                {this.props.blocType.category_id.removable && !this.props.signature ? (
                                    <div className="col-lg-2 col-md-2 col-sm-2 col-2 without-side-padding">
                                        <IconButton
                                            color="inherit"
                                            aria-label="Supprimer"
                                            onClick={this.deleteBloc.bind(this, this.props.currentBlocId)}
                                        >
                                            <DeleteIcon className="red-icon" />
                                        </IconButton>
                                    </div>
                                ) : null}
                                <div className="without-side-padding offset-lg-2 col-lg-10 offset-md-2 col-md-10 offset-sm-2 col-sm-10 offset-2 col-10">
                                    <Typography className={classes.italic}>{blocSubTitle}</Typography>
                                </div>
                            </div>
                        )}
                    </AccordionSummary>
                    {!this.props.creationBloc ? (
                        <AccordionDetails className="intervention-panel-details">
                            <div className="col-lg-12 col-md-12 col-sm-12 col-12 row padding-bloc-fields">
                                {fieldsElements}
                            </div>
                        </AccordionDetails>
                    ) : null}
                </Accordion>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        blocs: state.intervention.blocs,
        blocTypes: state.interventions.blocTypes,
        preview: state.intervention.preview,
        controlSignature: state.intervention.controlSignature,
        signature: state.intervention.signature,
        intervention: state.intervention.currentIntervention,
        loggedEmployee: state.loggedEmployee.loggedEmployee,
        fluidBottles: state.liquidBottles.myLiquidBottles,
        activeUser: state.activeUser,
        expanded: state.intervention.expandedBloc,
        sppFound: state.analyseRisques.sppFound,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setCurrentIntervention: (intervention, blocs) => dispatch(setCurrentIntervention(intervention, blocs)),
        throwErrorMessage: (errorObject) => dispatch(throwErrorMessage(errorObject)),
        validateBloc: (bloc, intervention, blocs, blocTypes, blocType, stateAccessor, user, previewMode) =>
            dispatch(validateBloc(bloc, intervention, blocs, blocTypes, blocType, stateAccessor, user, previewMode)),
        setExpandedBloc: (blocId) => dispatch(setExpandedBloc(blocId)),
    };
};

export default compose(withStyles(styles), connect(mapStateToProps, mapDispatchToProps))(InterventionReportBloc);
